import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Col, Image, Row, Select, Space, Spin, Switch, Tag, Typography, Upload } from 'antd';
import { CheckOutlined, DeleteOutlined, InboxOutlined, StopOutlined } from '@ant-design/icons';
import { trpc, trpcUntypedClient } from '../../lib/trpc';
import { useAuthStore } from '../../stores/authStore';
import { PURCHASE_ORDER_APPROVAL_SETTINGS_ANCHOR_ID } from '../../constants/purchaseOrderApprovalSettings';

const { Text, Title } = Typography;
const { Dragger } = Upload;

type PurchaseOrderApprovalSettings = {
  approvalRequired: boolean;
  showCompanySealOnApprovedPdf: boolean;
  approverUserIds: string[];
  defaultApproverUserId: string | null;
};

type PurchaseOrderApproverCandidate = {
  id: string;
  name: string | null;
  displayName: string | null;
  email: string | null;
  role: string;
  label: string;
};

type CompanySealView = {
  id: string;
  status: 'pending_approval' | 'active' | 'rejected' | 'disabled';
  imageUrl: string | null;
  transparentFileId: string;
  uploadedBy: string;
  approvedBy?: string | null;
  approvedByUser?: {
    id: string;
    name: string | null;
    displayName: string | null;
    email: string | null;
    label: string;
  } | null;
  approvedAt?: string | Date | null;
  rejectedAt?: string | Date | null;
};

type CompanySealState = {
  active: CompanySealView | null;
  pending: CompanySealView | null;
};

type CompanySealSettingsProps = {
  companyId: string;
  isManager: boolean;
  isAdmin: boolean;
};

const defaultApprovalSettings: PurchaseOrderApprovalSettings = {
  approvalRequired: false,
  showCompanySealOnApprovedPdf: true,
  approverUserIds: [],
  defaultApproverUserId: null,
};

const formatDateTime = (value?: string | Date | null) =>
  value ? new Date(value).toLocaleString('ja-JP') : '-';

const SealPreview = ({
  title,
  seal,
  tagColor,
  tagLabel,
}: {
  title: string;
  seal: CompanySealView | null;
  tagColor: string;
  tagLabel: string;
}) => (
  <section style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, minHeight: 148 }}>
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space>
        <Text strong>{title}</Text>
        <Tag color={tagColor}>{tagLabel}</Tag>
      </Space>
      {seal?.imageUrl ? (
        <Image
          src={seal.imageUrl}
          alt={title}
          style={{ maxWidth: 180, maxHeight: 96, objectFit: 'contain' }}
          preview={false}
        />
      ) : (
        <Text type="secondary">未設定</Text>
      )}
      {seal && (
        seal.status === 'active' ? (
          <Space direction="vertical" size={0}>
            <Text type="secondary">{`承認日時: ${formatDateTime(seal.approvedAt)}`}</Text>
            <Text type="secondary">{`承認者: ${seal.approvedByUser?.label ?? '-'}`}</Text>
          </Space>
        ) : (
          <Text type="secondary">{`登録ID: ${seal.id}`}</Text>
        )
      )}
    </Space>
  </section>
);

const CompanySealSettings: React.FC<CompanySealSettingsProps> = ({ companyId, isManager, isAdmin }) => {
  const [settings, setSettings] = useState<PurchaseOrderApprovalSettings>(defaultApprovalSettings);
  const [approverCandidates, setApproverCandidates] = useState<PurchaseOrderApproverCandidate[]>([]);
  const [sealState, setSealState] = useState<CompanySealState>({ active: null, pending: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { message } = App.useApp();
  const { user } = useAuthStore();

  const getUploadUrlMutation = trpc.file.getUploadUrl.useMutation();
  const registerFileMutation = trpc.file.registerFile.useMutation();
  const verifyUploadMutation = trpc.file.verifyUpload.useMutation();

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [nextSettings, nextSealState, nextApproverCandidates] = await Promise.all([
        trpcUntypedClient.query('company.getPurchaseOrderApprovalSettings', { companyId }) as Promise<PurchaseOrderApprovalSettings>,
        trpcUntypedClient.query('company.getCompanySeal', { companyId }) as Promise<CompanySealState>,
        trpcUntypedClient.query('company.getPurchaseOrderApproverCandidates', { companyId }) as Promise<PurchaseOrderApproverCandidate[]>,
      ]);
      setSettings({
        ...defaultApprovalSettings,
        ...(nextSettings ?? {}),
        approverUserIds: nextSettings?.approverUserIds ?? [],
        defaultApproverUserId: nextSettings?.defaultApproverUserId ?? null,
      });
      setSealState(nextSealState ?? { active: null, pending: null });
      setApproverCandidates(nextApproverCandidates ?? []);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '会社設定の取得に失敗しました';
      message.error(messageText);
    } finally {
      setLoading(false);
    }
  }, [companyId, message]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const saveSettings = async () => {
    if (
      settings.approvalRequired
      && (settings.approverUserIds.length === 0 || !settings.defaultApproverUserId)
    ) {
      message.warning('承認必須にする場合は承認可能ユーザーとデフォルト承認先を選択してください');
      return;
    }

    setSaving(true);
    try {
      const next = await trpcUntypedClient.mutation('company.updatePurchaseOrderApprovalSettings', {
        companyId,
        approvalRequired: settings.approvalRequired,
        showCompanySealOnApprovedPdf: settings.showCompanySealOnApprovedPdf,
        approverUserIds: settings.approverUserIds,
        defaultApproverUserId: settings.defaultApproverUserId,
      }) as PurchaseOrderApprovalSettings;
      setSettings({
        ...defaultApprovalSettings,
        ...next,
        approverUserIds: next.approverUserIds ?? [],
        defaultApproverUserId: next.defaultApproverUserId ?? null,
      });
      message.success('部品発注承認設定を保存しました');
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '部品発注承認設定の保存に失敗しました';
      message.error(messageText);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const presignedData = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category: 'company-seal',
      });

      const formData = new FormData();
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      await fetch(presignedData.url, {
        method: 'POST',
        body: formData,
        mode: 'no-cors',
      });

      await verifyUploadMutation.mutateAsync({ key: presignedData.key });

      const fileRecord = await registerFileMutation.mutateAsync({
        s3Key: presignedData.key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        category: 'company-seal',
        associations: [{
          entityType: 'company',
          entityId: companyId,
          associationType: 'company-seal-original',
        }],
      });

      await trpcUntypedClient.mutation('company.createCompanySealFromUploadedFile', {
        companyId,
        fileId: fileRecord.id,
      });
      message.success('社印を登録しました');
      await reload();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '社印の登録に失敗しました';
      message.error(messageText);
    } finally {
      setUploading(false);
    }
  };

  const handleSealAction = async (action: 'approve' | 'reject' | 'disable', sealId: string) => {
    const endpoint = {
      approve: 'company.approveCompanySeal',
      reject: 'company.rejectCompanySeal',
      disable: 'company.disableCompanySeal',
    }[action];
    const successMessage = {
      approve: '社印を有効化しました',
      reject: '社印を却下しました',
      disable: '社印を無効化しました',
    }[action];

    setSaving(true);
    try {
      await trpcUntypedClient.mutation(endpoint, { companyId, sealId });
      message.success(successMessage);
      await reload();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '社印設定の更新に失敗しました';
      message.error(messageText);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const isPendingSealUploadedByCurrentUser =
    Boolean(sealState.pending && user?.id && sealState.pending.uploadedBy === user.id);
  const canApprovePendingSeal =
    Boolean(sealState.pending) && (!isPendingSealUploadedByCurrentUser || isAdmin);
  const selectedApproverIds = settings.approverUserIds;
  const selectedApproverOptions = approverCandidates
    .filter((candidate) => selectedApproverIds.includes(candidate.id))
    .map((candidate) => ({
      label: candidate.label,
      value: candidate.id,
    }));
  const approverOptions = approverCandidates.map((candidate) => ({
    label: candidate.email ? `${candidate.label} (${candidate.email})` : candidate.label,
    value: candidate.id,
  }));
  const isApprovalSettingsIncomplete =
    settings.approvalRequired
    && (settings.approverUserIds.length === 0 || !settings.defaultApproverUserId);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <section id={PURCHASE_ORDER_APPROVAL_SETTINGS_ANCHOR_ID} style={{ scrollMarginTop: 88 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space direction="vertical" size={2}>
            <Title level={4} style={{ margin: 0 }}>部品発注設定</Title>
            <Text type="secondary">部品発注の承認要否、承認先、承認済みPDFの社印表示を管理します</Text>
          </Space>

          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2}>
                <Text strong>部品発注に承認を必要にする</Text>
                <Text type="secondary">承認済みになるまで正式PDFと発注済み化を止めます</Text>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Switch
                checked={settings.approvalRequired}
                disabled={!isManager || saving}
                onChange={(checked) => setSettings((current) => ({ ...current, approvalRequired: checked }))}
              />
            </Col>
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2}>
                <Text strong>承認済みPDFへ社印を載せる</Text>
                <Text type="secondary">有効な社印がある承認済み発注だけに反映します</Text>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Switch
                checked={settings.showCompanySealOnApprovedPdf}
                disabled={!isManager || saving}
                onChange={(checked) => setSettings((current) => ({
                  ...current,
                  showCompanySealOnApprovedPdf: checked,
                }))}
              />
            </Col>
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2}>
                <Text strong>承認可能ユーザー</Text>
                <Text type="secondary">部品発注を承認できる管理者またはマネージャー</Text>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="承認可能ユーザーを選択"
                value={settings.approverUserIds}
                disabled={!isManager || saving}
                options={approverOptions}
                onChange={(nextIds) => setSettings((current) => {
                  const nextDefault = current.defaultApproverUserId && nextIds.includes(current.defaultApproverUserId)
                    ? current.defaultApproverUserId
                    : nextIds[0] ?? null;
                  return {
                    ...current,
                    approverUserIds: nextIds,
                    defaultApproverUserId: nextDefault,
                  };
                })}
              />
            </Col>
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2}>
                <Text strong>デフォルト承認先</Text>
                <Text type="secondary">新規発注で初期選択する承認先</Text>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="デフォルト承認先を選択"
                value={settings.defaultApproverUserId ?? undefined}
                disabled={!isManager || saving || selectedApproverOptions.length === 0}
                options={selectedApproverOptions}
                onChange={(value) => setSettings((current) => ({
                  ...current,
                  defaultApproverUserId: value ?? null,
                }))}
              />
            </Col>
          </Row>

          {isApprovalSettingsIncomplete && (
            <Alert
              type="warning"
              showIcon
              message="承認必須にする場合は承認可能ユーザーとデフォルト承認先を設定してください"
            />
          )}

          {isManager ? (
            <Button type="primary" loading={saving} onClick={() => void saveSettings()}>
              設定を保存
            </Button>
          ) : (
            <Alert type="warning" showIcon message="この設定を変更するにはマネージャー権限が必要です" />
          )}
        </Space>
      </section>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <SealPreview title="有効な社印" seal={sealState.active} tagColor="success" tagLabel="有効" />
        </Col>
        <Col xs={24} md={12}>
          <SealPreview title="承認待ちの社印" seal={sealState.pending} tagColor="warning" tagLabel="承認待ち" />
        </Col>
      </Row>

      {isManager && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {isPendingSealUploadedByCurrentUser && (
            <Alert
              type={isAdmin ? 'info' : 'warning'}
              showIcon
              message={
                isAdmin
                  ? '管理者は本人が登録した社印も承認できます'
                  : 'アップロード者本人は社印を承認できません'
              }
            />
          )}
          <Dragger
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              if (file.size > 10 * 1024 * 1024) {
                message.error('ファイルサイズは10MB以下にしてください');
                return false;
              }
              if (!file.type.startsWith('image/')) {
                message.error('画像ファイルを選択してください');
                return false;
              }
              void handleUpload(file);
              return false;
            }}
            disabled={uploading || saving}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{uploading ? 'アップロード中...' : '社印画像をアップロード'}</p>
          </Dragger>

          <Space wrap>
            <Button
              icon={<CheckOutlined />}
              type="primary"
              disabled={!canApprovePendingSeal}
              loading={saving}
              onClick={() => sealState.pending && void handleSealAction('approve', sealState.pending.id)}
            >
              社印を承認して有効化
            </Button>
            <Button
              icon={<StopOutlined />}
              disabled={!sealState.pending}
              loading={saving}
              onClick={() => sealState.pending && void handleSealAction('reject', sealState.pending.id)}
            >
              社印を却下
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={!sealState.active}
              loading={saving}
              onClick={() => sealState.active && void handleSealAction('disable', sealState.active.id)}
            >
              有効な社印を無効化
            </Button>
          </Space>
        </Space>
      )}
    </Space>
  );
};

export default CompanySealSettings;
